/**
 *  @module util/loacalStorage - Helpers for working with local storage
 *
 * @version 1.0.0
 * @copyright viax.io 2019
 */

import log from '@/utils/logger';

import { LOCAL_STORAGE_PREFIX } from '@/utils';

/**
 * Helper that adds the namespace prefix to every local storage key
 *
 * @param {string} key - Key
 */
const keyWithPrefix = (key) => `${LOCAL_STORAGE_PREFIX}${key}`;

/**
 * Saves a value to local storage
 *
 * @param {string} key - Key to save the data under
 * @param {*} value - Data to save
 *
 * @since Version 1.0.0
 */
const setValue = (key, value) => {
  localStorage.setItem(keyWithPrefix(key), JSON.stringify(value));
};

/**
 * Saves an array to local storage
 *
 * @param {string} key - Key to save the data under
 * @param {*} data - Data to save
 *
 * @since Version 1.0.0
 */
const setArray = (key, value) => {
  const array = value === null ? [] : value;
  localStorage.setItem(keyWithPrefix(key), JSON.stringify(array));
};

/**
 * Loads a value from local storage
 *
 * @param {string} key - Key to load the data from
 *
 * @since Version 1.0.0
 */
const getValue = (key) => {
  const fullKey = keyWithPrefix(key);
  const stored = localStorage.getItem(fullKey);
  if (stored !== null) {
    try {
      return JSON.parse(stored);
    } catch (error) {
      log.error(`Error in parsing value from local storage key ${fullKey}: ${error}`);
    }
  }
  return null;
};

/**
 * Loads an array from local storage
 *
 * @param {string} key - Key to load the data from
 *
 * @since Version 1.0.0
 */
const getArray = (key) => {
  const fullKey = keyWithPrefix(key);
  const stored = localStorage.getItem(fullKey);
  if (stored !== null) {
    try {
      return JSON.parse(stored);
    } catch (error) {
      log.error(`Error in parsing value from local storage key ${fullKey}: ${error}`);
    }
  }
  return [];
};

/**
 * Removes a value from local storage
 *
 * @param {string} key - Key to remove the data from
 *
 * @since Version 1.0.0
 */
const remove = (key) => {
  localStorage.removeItem(keyWithPrefix(key));
};

export default {
  getArray,
  getValue,
  remove,
  setArray,
  setValue,
};
